/**
 * Agreeing a new price on a consignment that stays where it is.
 *
 * The gap OND-101 names: the asking price and floor were set once, on the offer,
 * and nothing in the product could change them. The only way to reprice was to
 * end the arrangement and offer it again — which stood down any agent on the
 * work, emptied the gallery's inventory row for as long as it took, and left a
 * trail reading like a deal that had collapsed.
 *
 * ONE PANEL, MOUNTED ON BOTH SIDES. The owner reads it under the consignment on
 * a work's own record; the gallery reads it on the inventory row it is holding.
 * Either may propose; both must accept. Which of those the reader is comes from
 * the server — `yoursToAnswer` — because a party may be a firm the reader is a
 * member of, and the browser has no way to ask about memberships.
 *
 * THE COPY NEVER PROMISES A PRICE HAS MOVED UNTIL IT HAS. Every sentence about
 * a pending proposal says what is still true, because the thing a reader most
 * needs to know about a proposal is that it has changed nothing.
 */
export const REVISIONS_COPY = {
  title: "Terms",
  loading: "Reading the terms…",

  /** When the reader may read the consignment but the terms could not be read. */
  unavailable: "These terms are not open to this account.",

  /** The terms in force, however they got there. */
  inForce: "In force",
  /** 'As offered' for version 0, 'Version 3' after. */
  asOffered: "As offered",
  version: (n: number) => `Version ${n}`,
  since: "since",

  terms: {
    basis: "Basis",
    asking: "Asking",
    floor: "Floor",
    note: "Note",
  },
  /** Shown in place of a figure that was never agreed. Never £0. */
  noFigure: "—",

  basis: {
    net_of_sale: "Net of sale",
    min_price_agreed: "Minimum price agreed",
    unspecified: "Unspecified",
  },

  /** The floor, said once, where the reader is deciding about it. */
  floorNote:
    "A sale below the floor in force is refused by the registry, not merely warned about. A lower floor applies only once both of you have accepted it.",

  pending: {
    heading: "Awaiting an answer",
    /** 'Proposed by Hauser & Wirth (ONDO-6624) on 04 Feb 2026'. */
    proposedBy: "Proposed by",
    on: "on",
    /** Under a proposal the reader cannot answer, because it is their own side's. */
    waiting:
      "Nothing changes until the other party accepts these exact terms. The terms in force above are unaffected, and they may refuse.",
    /** Under a proposal the reader owes an answer to. */
    yours:
      "Accepting these terms puts them in force the moment you do. Refusing leaves the arrangement exactly as it is — it does not end the consignment.",
    accept: "Accept these terms",
    refuse: "Refuse them",
    accepting: "Accepting…",
    refusing: "Refusing…",
  },

  /** Each party's answer to the version being shown. */
  agreement: {
    heading: "Agreed by",
    gallery: "Gallery",
    owner: "Owner",
    /** Appended to whichever side the reader acts for. */
    you: "you",
    accepted: "Accepted",
    refused: "Refused",
    awaiting: "Not yet answered",
  },

  history: {
    heading: "Earlier versions",
    /** Shown when nothing has ever been proposed — which is the ordinary case. */
    none: "These are the terms as they were offered. Nothing has been revised.",
    accepted: "Agreed",
    rejected: "Refused",
    lapsed: "Lapsed — the consignment ended before it was answered",
  },

  propose: {
    open: "Propose new terms",
    title: "Propose new terms",
    lead:
      "Both of you have to accept the same figures before anything changes. The consignment carries on either way, and any agent on the work stays in place.",
    basis: "Basis",
    currency: "Currency",
    asking: "Asking price",
    askingHelp: "Optional. Leave it blank to agree no asking price.",
    floor: "Floor",
    floorHelp: "Optional. The least the work may be sold for once this is agreed.",
    note: "Note",
    noteHelp: "Optional. A remark about this proposal, not about the arrangement.",
    submit: "Send the proposal",
    sending: "Sending…",
    cancel: "Cancel",
    sent: "Proposed. The other party has been told, and nothing changes until they accept.",
  },

  errors: {
    invalidMoney: "That is not an amount.",
    floorAboveAsking: "The floor cannot be above the asking price.",
    currencyNeeded: "Name a currency for the figures.",
    unchanged: "Those are already the terms in force.",
    /** `ondo.propose_consignment_revision()` raises 23505 for this. */
    alreadyPending: "There is already a proposal awaiting an answer.",
    refused: "That cannot be done by this account.",
    gone: "That proposal is no longer open. Re-read the terms.",
  },
} as const;
